import {
  useCallback, useEffect, useRef, useState
} from 'react'
import {
  WhatsAppPairingModal
} from '../WhatsAppPairingModal'
import {
  SlackPairingModal
} from '../SlackPairingModal'
import {
  SMSPairingModal
} from '../SMSPairingModal'
import {
  IMessagePairingModal
} from '../IMessagePairingModal'
import {
  OnboardingWizardEvents,
  trackEvent
} from '../../analytics/posthog'
import {
  ArrowRight, Smartphone
} from 'lucide-react'
import type {
  ChannelType,
  ConnectedChannel
} from '../../hooks/useOnboardingState'
import {
  isPersonalChannel
} from '../../hooks/useOnboardingState'
import {
  API_BASE_URL
} from '../../config'
import {
  imessageService, slackService, whatsappService
} from '../../services/api'
import {
  clearChannelResume,
  isResumableChannel,
  readChannelResume,
  writeChannelResume,
  type ResumableChannel
} from './channelResume'

const isLocalDev = API_BASE_URL.includes('localhost')

const WhatsAppIcon = () => (
  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.594z"/>
  </svg>
)

const SlackIcon = () => (
  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
  </svg>
)

interface ChannelOption {
  id: ChannelType
  name: string
  icon: JSX.Element
  accentColor: string
  bgColor: string
  textColor: string
}

interface ChannelConnectionStepProps {
  assistantName?: string | undefined
  userId?: number | undefined
  smsFeatureEnabled: boolean
  onConnected: (channel: ConnectedChannel) => void
}

const WHATSAPP_CHANNEL: ChannelOption = {
  id: 'whatsapp',
  name: 'WhatsApp',
  icon: <WhatsAppIcon />,
  accentColor: 'border-green-300 hover:border-green-400',
  bgColor: 'bg-green-50',
  textColor: 'text-green-600',
}

const SLACK_CHANNEL: ChannelOption = {
  id: 'slack',
  name: 'Slack',
  icon: <SlackIcon />,
  accentColor: 'border-purple-300 hover:border-purple-400',
  bgColor: 'bg-purple-50',
  textColor: 'text-purple-600',
}

const SMS_CHANNEL: ChannelOption = {
  id: 'sms',
  name: 'SMS',
  icon: <Smartphone className="w-7 h-7" />,
  accentColor: 'border-blue-300 hover:border-blue-400',
  bgColor: 'bg-blue-50',
  textColor: 'text-blue-600',
}

const IMESSAGE_CHANNEL: ChannelOption = {
  id: 'imessage',
  name: 'iMessage',
  icon: <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" clipRule="evenodd" d="M5 1a4 4 0 0 0-4 4v14a4 4 0 0 0 4 4h14a4 4 0 0 0 4-4V5a4 4 0 0 0-4-4H5Zm1.653 17.387.002-.002.008-.007.032-.032a5.243 5.243 0 0 0 .494-.575c.236-.32.435-.675.494-.997-1.91-1.163-3.183-3.092-3.183-5.298C4.5 7.852 7.91 5 12 5s7.5 2.853 7.5 6.477c0 3.625-3.41 6.478-7.5 6.478-.572 0-1.13-.056-1.666-.16-.405.575-1.044.884-1.651 1.044a5.308 5.308 0 0 1-1.835.136l-.734-.082.54-.505Z" /></svg>,
  accentColor: 'border-sky-300 hover:border-sky-400',
  bgColor: 'bg-sky-50',
  textColor: 'text-sky-600',
}

export function ChannelConnectionStep({
  assistantName,
  userId,
  smsFeatureEnabled,
  onConnected,
}: ChannelConnectionStepProps): JSX.Element {
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false)
  const [showSlackModal, setShowSlackModal] = useState(false)
  const [showSmsModal, setShowSmsModal] = useState(false)
  const [showImessageModal, setShowImessageModal] = useState(false)
  const [resumeChannel, setResumeChannel] = useState<ResumableChannel | null>(
    () => readChannelResume(userId),
  )
  const handledConnectionsRef = useRef<Set<ChannelType>>(new Set())
  const resumeCheckInFlight = useRef(false)
  const pairingModalOpen = showWhatsAppModal
    || showSlackModal
    || showSmsModal
    || showImessageModal

  useEffect(() => {
    trackEvent(OnboardingWizardEvents.CHANNEL_STEP_VIEWED)
  }, [])

  useEffect(() => {
    setResumeChannel(readChannelResume(userId))
  }, [userId])

  const name = assistantName || 'your assistant'

  const channels: ChannelOption[] = [
    WHATSAPP_CHANNEL,
    SLACK_CHANNEL,
    ...(smsFeatureEnabled ? [SMS_CHANNEL] : []),
    IMESSAGE_CHANNEL,
  ]

  const tierFor = (channel: ChannelType): 'personal' | 'work' =>
    isPersonalChannel(channel) ? 'personal' : 'work'

  const openChannelModal = (channelId: ChannelType): void => {
    if (channelId === 'whatsapp') setShowWhatsAppModal(true)
    else if (channelId === 'slack') setShowSlackModal(true)
    else if (channelId === 'sms') setShowSmsModal(true)
    else if (channelId === 'imessage') setShowImessageModal(true)
  }

  const handleChannelClick = (channelId: ChannelType, resumed = false): void => {
    trackEvent(OnboardingWizardEvents.CHANNEL_CONNECT_CLICKED, {
      channel: channelId,
      resumed,
      tier: tierFor(channelId),
    })
    if (!isResumableChannel(channelId)) {
      clearChannelResume(userId)
      setResumeChannel(null)
    }
    openChannelModal(channelId)
  }

  const handleAttemptStarted = useCallback((
    channel: ResumableChannel,
    expiresAt?: string,
  ): void => {
    writeChannelResume(userId, channel, expiresAt)
    setResumeChannel(channel)
  }, [userId])

  const handleSuccess = useCallback((channel: ChannelType): void => {
    if (handledConnectionsRef.current.has(channel)) return
    handledConnectionsRef.current.add(channel)

    trackEvent(OnboardingWizardEvents.CHANNEL_CONNECTED, {
      channel,
      tier: tierFor(channel),
    })
    setShowWhatsAppModal(false)
    setShowSlackModal(false)
    setShowSmsModal(false)
    setShowImessageModal(false)
    clearChannelResume(userId)
    setResumeChannel(null)
    onConnected(channel)
  }, [onConnected, userId])

  useEffect(() => {
    // An open pairing modal owns polling for its active attempt. Waiting until
    // it closes avoids racing that poll with this page-level recovery check.
    if (resumeChannel == null || pairingModalOpen) return

    let cancelled = false
    const checkConnection = async (): Promise<void> => {
      if (resumeCheckInFlight.current) return
      resumeCheckInFlight.current = true
      try {
        const status = resumeChannel === 'whatsapp'
          ? await whatsappService.getStatus()
          : resumeChannel === 'slack'
            ? await slackService.getStatus()
            : await imessageService.getStatus()
        if (!cancelled && status.connected) {
          handleSuccess(resumeChannel)
        }
      } catch {
        // A transient status failure should leave the resume affordance intact.
      } finally {
        resumeCheckInFlight.current = false
      }
    }

    let foregroundCheckTimeout: ReturnType<typeof setTimeout> | undefined
    const scheduleForegroundCheck = (): void => {
      if (foregroundCheckTimeout) clearTimeout(foregroundCheckTimeout)
      foregroundCheckTimeout = setTimeout(() => void checkConnection(), 100)
    }
    const checkOnVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') scheduleForegroundCheck()
    }

    void checkConnection()
    window.addEventListener('focus', scheduleForegroundCheck)
    document.addEventListener('visibilitychange', checkOnVisibilityChange)
    return () => {
      cancelled = true
      if (foregroundCheckTimeout) clearTimeout(foregroundCheckTimeout)
      window.removeEventListener('focus', scheduleForegroundCheck)
      document.removeEventListener('visibilitychange', checkOnVisibilityChange)
    }
  }, [handleSuccess, pairingModalOpen, resumeChannel])

  const renderTile = (channel: ChannelOption): JSX.Element => (
    <button
      key={channel.id}
      onClick={() => handleChannelClick(channel.id)}
      aria-label={`Connect ${channel.name}`}
      className={`flex flex-col items-center justify-center rounded-2xl border-2 bg-white transition-[border-color,box-shadow,transform] duration-200 hover:shadow-soft hover:scale-[1.03] cursor-pointer gap-2.5 p-6 ${channel.accentColor}`}
    >
      <div className={`rounded-2xl flex items-center justify-center w-14 h-14 ${channel.bgColor} ${channel.textColor}`}>
        {channel.icon}
      </div>
      <span className="font-semibold text-gray-900">{channel.name}</span>
      <span className="inline-flex items-center gap-1 font-semibold text-primary-600 text-xs">
        Connect
        <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </button>
  )

  const resumeOption = resumeChannel == null
    ? null
    : channels.find((channel) => channel.id === resumeChannel) ?? null
  const resumeCard = resumeOption == null ? null : (
    <div
      className="max-w-md mx-auto mb-6 rounded-2xl border border-primary-200 bg-primary-50 px-4 py-4 sm:flex sm:items-center sm:justify-between sm:gap-4"
      aria-live="polite"
    >
      <div>
        <p className="font-semibold text-gray-900">
          Finish connecting {resumeOption.name}
        </p>
        <p className="mt-1 text-sm text-gray-600">
          Your setup is saved. Continue where you left off, and we'll detect the connection automatically.
        </p>
      </div>
      <button
        type="button"
        onClick={() => handleChannelClick(resumeOption.id, true)}
        className="mt-3 shrink-0 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 sm:mt-0"
      >
        Continue setup
      </button>
    </div>
  )

  return (
    <div className="animate-slide-in-right">
      <div className="text-center mb-10">
        <h2 className="text-3xl sm:text-4xl font-display uppercase tracking-tight text-gray-900">
          How would you like to chat with {name}?
        </h2>
      </div>

      {resumeCard}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-md mx-auto">
        {channels.map((channel) => renderTile(channel))}
      </div>

      {isLocalDev && (
        <div className="text-center mt-6">
          <button
            onClick={() => onConnected('whatsapp')}
            className="text-sm text-gray-400 hover:text-gray-600 underline"
          >
            Skip for now (local dev only)
          </button>
        </div>
      )}

      <WhatsAppPairingModal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        onAttemptStarted={(expiresAt) => {
          handleAttemptStarted('whatsapp', expiresAt)
        }}
        onSuccess={() => handleSuccess('whatsapp')}
      />
      <SlackPairingModal
        isOpen={showSlackModal}
        onClose={() => setShowSlackModal(false)}
        onAttemptStarted={() => handleAttemptStarted('slack')}
        onSuccess={() => handleSuccess('slack')}
      />
      <SMSPairingModal
        isOpen={showSmsModal}
        onClose={() => setShowSmsModal(false)}
        onSuccess={() => handleSuccess('sms')}
      />
      <IMessagePairingModal
        isOpen={showImessageModal}
        onClose={() => setShowImessageModal(false)}
        onAttemptStarted={(expiresAt) => {
          handleAttemptStarted('imessage', expiresAt)
        }}
        onSuccess={() => handleSuccess('imessage')}
      />
    </div>
  )
}
